import { orderItem } from 'src/shared/frontendTypes/warenkorb';
import { tcoupon } from 'src/shared/frontendTypes/coupon';
import config from 'src/config';
import { getCartLinePrice } from './cartPriceLock'
import { getFreightShippingCost, hasFreightShippingItems } from './shippingAvailability'
import {
  isCouponPercentBalance,
  parseCouponBalanceValue,
} from 'src/shared/couponBalance'

const { 
  mwst: globalMwst,
  mindermengenzuschlag,
  versandkosten,
} = config;

const getTotals = (
  items: Record<string, orderItem>,
  country?: {
    mwst?: number;
    eu: boolean;
    iso: string;
    DhlZone?: number;
  },
  coupon?: tcoupon
) => {
  const getItemLinePrice = (item: orderItem) =>
    getCartLinePrice(item.price, item.qty, {
      onlyStock: item.onlyStock,
      stock: item.stock,
    })

  const getDiscountableItemLinePrice = (item: orderItem) => {
    const { price, qty } = item
    if (typeof price === 'number') return price * qty
    return price.discountPrice === price.regularPrice ? price.regularPrice * qty : 0
  }

  const { 
    mwst: countryMwst = globalMwst,
    eu: isEu = true,
    iso = 'DE',
    DhlZone = 8,
  } = country || {};
  const hasSpeditionsversand = hasFreightShippingItems(items);

  // Berechnete Gesamtsumme des Warenkorbs
  const WarenkorbDeutschlandBrutto = Object.values(items).reduce(
    (a, item) => a + getItemLinePrice(item),
    0
  );

  // Berechnete Gesamtsumme der Artikel ohne Rabatt
  const WarenkorbDeutschlandBruttoOhneRabatt = Object.values(items).reduce(
    (a, item) => a + getDiscountableItemLinePrice(item),
    0
  );

  // Nettosumme des Warenkorbs ohne unsere Deutsche MwSt.
  const WarenkorbNetto = WarenkorbDeutschlandBrutto / (1 + globalMwst / 100);
  const WarenkorbNettoOhneRabatt = WarenkorbDeutschlandBruttoOhneRabatt / (1 + globalMwst / 100);
    
  // Die Tatsächliche Brutto-Summe des Warenkorbs
  const WarenkorbBrutto = countryMwst === globalMwst
    ? WarenkorbDeutschlandBrutto
    : WarenkorbNetto * (1 + countryMwst / 100);

  const WarenkorbBruttoOhneRabatt = countryMwst === globalMwst
    ? WarenkorbDeutschlandBruttoOhneRabatt
    : WarenkorbNettoOhneRabatt * (1 + countryMwst / 100);

  // Die Summe der MwSt. des Warenkorbs, OHNE Versandkosten und Mindermengenzuschlag.
  const WarenkorbEnthalteneMwSt = WarenkorbBrutto - WarenkorbNetto;

  // Berechnung der Versandkosten
  const getVersandkosten = () => {
    if (hasSpeditionsversand) return getFreightShippingCost(iso) ?? 0;
    if (iso === 'DE') return versandkosten.deutschland;
    if (!isEu) return versandkosten.ausserhalbEuropa;
    if (DhlZone === 1) return versandkosten.dhlZohne1;
    if (DhlZone === 2) return versandkosten.dhlZohne2;
    if (DhlZone === 3) return versandkosten.dhlZohne3;
    return versandkosten.ausserhalbEuropa;
  };

  const Versandkosten = getVersandkosten();

  // Berechnung des Mindermengenzuschlags
  const getMindermengenzuschlag = () => {
    if (hasSpeditionsversand) return 0;
    if (WarenkorbBrutto < mindermengenzuschlag.entfaelltAb) return mindermengenzuschlag.betrag;
    return 0;
  };

  const Mindermengenzuschlag = getMindermengenzuschlag();

  // Berechnung der Summen nach Brands
  const brandsTotals = Object.values(items).reduce((acc, item) => {
    const { brand } = item
    const brandId = String(brand?.id || 'unknown'); // assuming item has a brand property with id
    const itemTotal = getItemLinePrice(item);

    if (!acc[brandId]) {
      acc[brandId] = { total: 0, title: brand?.titel || '' };
    }
    acc[brandId].total += itemTotal;

    return acc;
  }, {} as Record<string, { total: number, title: string }>);

  const toNumberSet = (values?: number[]) =>
    new Set((values || []).map(value => Number(value)).filter(Number.isFinite))
  const toNumberArray = (values?: unknown) =>
    Array.isArray(values)
      ? values.map(value => Number(value)).filter(Number.isFinite)
      : []
  const getItemBrandId = (item: orderItem) => {
    const brand = item.brand as orderItem['brand'] | number | undefined
    return typeof brand === 'number' ? brand : brand?.id
  }
  const getItemCategoryIds = (item: orderItem) =>
    new Set([
      item.categorie,
      ...toNumberArray((item as orderItem & {
        allCategories?: unknown[]
        categories?: unknown[]
      }).allCategories),
      ...toNumberArray((item as orderItem & {
        allCategories?: unknown[]
        categories?: unknown[]
      }).categories),
    ].map(value => Number(value)).filter(Number.isFinite))

  const couponCategoryIds = toNumberSet(coupon?.categories)
  const couponProductIds = toNumberSet(coupon?.products)
  const hasCouponItemScope = Boolean(
    coupon?.brand ||
    couponCategoryIds.size > 0 ||
    couponProductIds.size > 0,
  )

  const itemMatchesCouponScope = (item: orderItem) => {
    if (couponProductIds.size > 0) return couponProductIds.has(item.id)
    if (coupon?.brand && getItemBrandId(item) !== coupon.brand) return false
    if (couponCategoryIds.size > 0) {
      const itemCategoryIds = getItemCategoryIds(item)
      if (!Array.from(couponCategoryIds).some(category => itemCategoryIds.has(category))) return false
    }
    return true
  }

  const getCouponScopeTotal = () =>
    Object.values(items).reduce((sum, item) => {
      if (!itemMatchesCouponScope(item)) return sum
      return sum + (
        coupon?.stackable
          ? getItemLinePrice(item)
          : getDiscountableItemLinePrice(item)
      )
    }, 0)

  // Berechnung des Coupon-Rabattes
  const getCouponDiscount = () => {
    if (coupon && coupon.balance) {
      let discountBasis: number;
      const couponBalanceValue = parseCouponBalanceValue(coupon.balance)
      if (typeof couponBalanceValue !== 'number') return { discount: 0, basis: 0 }

      if (hasCouponItemScope) {
        const scopeTotal = getCouponScopeTotal()

        discountBasis = scopeTotal;
        
        if (isCouponPercentBalance(coupon.balance)) {
          const discount = couponBalanceValue / 100;
          const calculatedDiscount = scopeTotal * discount;
          return { discount: calculatedDiscount, basis: discountBasis };
        } else {
          const fixedDiscount = Math.min(couponBalanceValue, scopeTotal);
          return { discount: fixedDiscount, basis: discountBasis };
        }
      } else {
        discountBasis = coupon.stackable ? WarenkorbBrutto : WarenkorbBruttoOhneRabatt;
        if (isCouponPercentBalance(coupon.balance)) {
          const discount = couponBalanceValue / 100;
          const calculatedDiscount = discountBasis * discount;
          return { discount: calculatedDiscount, basis: discountBasis };
        } else {
          const fixedDiscount = couponBalanceValue;
          return { discount: fixedDiscount, basis: discountBasis };
        }
      }
    }
    return { discount: 0, basis: 0 };
  };

  const { discount: Rabatt, basis: Rabattgrundlage } = getCouponDiscount();
  
  // Die Gesamtsumme die vom KD zu zahlen ist.
  const Gesamtsumme = (isEu ? WarenkorbBrutto : WarenkorbNetto) + Versandkosten + Mindermengenzuschlag - Rabatt;

  // Gesamte MwSt. inkl. Warenkorb, Versandkosten und Mindermengenzuschlag.
  const EnthalteneMwSt = Gesamtsumme - (Gesamtsumme / (1 + countryMwst / 100));

  return {
    WarenkorbBrutto,
    WarenkorbBruttoOhneRabatt,
    WarenkorbNetto,
    WarenkorbEnthalteneMwSt,
    Versandkosten,
    Mindermengenzuschlag,
    Rabatt,
    Rabattgrundlage, // Rabattgrundlage hinzufügen
    EnthalteneMwSt,
    Gesamtsumme,
    brandsTotals, // Summen der Brands hinzufügen
  };
};

export default getTotals;
